import type {
    ChoiceModalOptions,
    ConfirmModalChoice,
    ConfirmModalOptions,
    InfoModalOptions,
    UnsavedChangesChoice,
    UnsavedChangesModalOptions,
} from "@/services/simple-modal/simple-modal-types";
import { useStore } from "@/store/store";

import { registerResolver, resolveChoice, resolveInfo } from "./resolver-registry";

export type {
    ChoiceModalOptions,
    ConfirmModalChoice,
    ConfirmModalOptions,
    InfoModalOptions,
    UnsavedChangesChoice,
    UnsavedChangesModalOptions,
};

/**
 * Closes any currently open modal before a new one is displayed. Evicted promise-based
 * modals settle as if they were dismissed.
 */
const settleExistingSimpleModal = (): void => {
    const { simpleModalDescriptor, closeSimpleModal } = useStore.getState();
    if (!simpleModalDescriptor) {
        return;
    }
    if (simpleModalDescriptor.type === "choice") {
        resolveChoice(simpleModalDescriptor.requestId, simpleModalDescriptor.dismissValue);
    } else {
        resolveInfo(simpleModalDescriptor.requestId);
    }
    closeSimpleModal();
};

/** Programmatically close and settle the currently open modal. */
export const closeModal = (): void => {
    settleExistingSimpleModal();
};

/**
 * Open a modal with an arbitrary set of buttons, resolving the value of whichever one settles it.
 * Prefer a hardwired variant below when one fits; reach for this when the buttons are one-off.
 */
export const choiceModal = <T extends string>(options: ChoiceModalOptions<T>): Promise<T> =>
    new Promise<T>((resolve, reject) => {
        settleExistingSimpleModal();
        const requestId = crypto.randomUUID();
        const dismissible = options.dismissible ?? true;
        // The host only ever echoes back a declared button value or the dismiss value.
        registerResolver(requestId, { type: "choice", resolve: (value) => resolve(value as T), reject });
        useStore.getState().openSimpleModal({
            type: "choice",
            requestId,
            title: options.title,
            body: options.body,
            buttons: options.buttons,
            dismissValue: options.dismissValue,
            showClose: options.showClose ?? dismissible,
            dismissible,
        });
    });

/**
 * Open a two-button confirm modal. Resolves "confirm" or "cancel"; dismissal resolves "cancel".
 */
export const confirmModal = (options: ConfirmModalOptions): Promise<ConfirmModalChoice> => {
    // Danger modals require an explicit action by default — they are non-dismissible unless overridden.
    const dismissible = options.dismissible ?? options.tone !== "danger";
    return choiceModal<ConfirmModalChoice>({
        title: options.title,
        body: options.body,
        buttons: [
            { label: options.cancelLabel ?? "Cancel", value: "cancel", onSelect: options.onDismiss },
            {
                label: options.confirmLabel ?? "Confirm",
                value: "confirm",
                tone: options.tone ?? "default",
                onSelect: options.onConfirm,
            },
        ],
        dismissValue: "cancel",
        dismissible,
        showClose: dismissible,
    });
};

/**
 * Open the standard unsaved-changes prompt. Resolves "save", "discard" or "keep-editing";
 * dismissal resolves "keep-editing", so closing the modal never drops the edits.
 */
export const unsavedChangesModal = (options: UnsavedChangesModalOptions = {}): Promise<UnsavedChangesChoice> =>
    choiceModal<UnsavedChangesChoice>({
        title: options.title ?? "You have unsaved changes",
        body: options.body,
        buttons: [
            { label: options.keepEditingLabel ?? "Keep editing", value: "keep-editing" },
            { label: options.discardLabel ?? "Discard changes", value: "discard", tone: "danger" },
            { label: options.saveLabel ?? "Save", value: "save", onSelect: options.onSave },
        ],
        dismissValue: "keep-editing",
    });

/**
 * Open an info modal.
 */
export const infoModal = (options: InfoModalOptions): Promise<void> => {
    settleExistingSimpleModal();
    const requestId = crypto.randomUUID();
    return new Promise<void>((resolve) => {
        registerResolver(requestId, { type: "info", resolve });
        useStore.getState().openSimpleModal({
            type: "info",
            requestId,
            title: options.title,
            body: options.body,
            closeLabel: options.closeLabel ?? "OK",
            tone: options.tone ?? "info",
            dismissible: options.dismissible ?? true,
        });
    });
};
